import React, { useEffect, useState } from "react";
import { Form, Button, Modal, Popconfirm, Spin } from "antd";
import { ReactSVG } from "react-svg";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import ListContainer from "../../modules/routePlan/containers/List";
import RoutePlanForm from "../Forms/RoutePlans/RoutePlan";
import RoutePlansTable from "../Table/RoutePlans";
import withPage from "./withPage";
import { PAGE_SIZE } from "../../constants";
import RoutePlanCurrent from "../RoutePlan/RoutePlanCurrent";
import PrimaryButton from "../Buttons/PrimaryButton";
import { companyDeactivated, getTranslation } from "../../utils/helper";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

// Extend Day.js with UTC plugin
dayjs.extend(utc);

const RoutePlansPage = ({
  isFetching,
  routePlans,
  metadata,
  message,
  query,
  pushTo,
  doGetAllRoutePlans,
  doCreateRoutePlan,
  doUpdateRoutePlan,
  doDeleteRoutePlan,
  doDuplicateRoutePlan,
  history,
}) => {
  const user = useSelector(state => state.auth.user);
  const setting = useSelector(state => state.setting.setting) || user?.company;

  const [form] = Form.useForm();
  const currentPage = Number(query.currentPage) || 1;
  const itemsPerPage = Number(query.itemsPerPage) || PAGE_SIZE;
  const pagination = {
    current: currentPage,
    pageSize: itemsPerPage,
    total: metadata.totalItems,
    showTotal: total => `${total} ${getTranslation(setting, "Route Plans", "Route Plans")}`,
  };
  const [state, setState] = useState({
    visibleModal: false,
    action: "",
    routePlan: null,
  });

  useEffect(() => {
    if (!user.roles.includes("user")) {
      doGetAllRoutePlans({ currentPage, itemsPerPage, ...query });
    }
  }, []);

  useEffect(() => {
    if (message?.includes("create_routeplan_succeeded")) {
      history.push(`/route-plans/${message.split("_")[3]}`);
    }
  }, [message]);

  const handleFilter = (params, updatePath) => {
    if (updatePath) {
      pushTo({ query: params });
    }
    const currentPage = Number(params.currentPage) || 1;
    const itemsPerPage = Number(params.itemsPerPage) || PAGE_SIZE;
    doGetAllRoutePlans({
      currentPage,
      itemsPerPage,
      ...params,
    });
  };

  const handleTableChange = params => {
    handleFilter(params, true);
  };

  const withAction = item => {
    return (
      <div className="actions">
        <Link to={"/route-plans/" + item._id} className="RoutePlanName">
          <Button type="link" className="view">
            <ReactSVG className="icon" src="/icons/visibility.svg" />
          </Button>
        </Link>
        <Button
          type="link"
          className="edit"
          onClick={() => {
            handleClickAction("edit", item);
          }}>
          <ReactSVG className="icon" src="/icons/edit.svg" />
        </Button>
        <Button onClick={() => handleClickDuplicate(item)} type="link" className="duplicate">
          <ReactSVG className="icon" src="/icons/control_point_duplicate.svg" />
        </Button>
        <Popconfirm
          title={getTranslation(setting, "Common", "Are you sure to delete this item?")}
          onConfirm={() => handleClickDelete(item)}
          okText={getTranslation(setting, "Common", "Yes")}
          cancelText={getTranslation(setting, "Common", "No")}>
          <Button type="link" className="delete">
            <ReactSVG className="icon" src="/icons/delete.svg" />
          </Button>
        </Popconfirm>
      </div>
    );
  };

  const handleClickAction = (action, item) => {
    form.setFieldsValue({
      name: item?.name,
      deliveryDate: item?.deliveryDate ? dayjs(item?.deliveryDate).utc() : null,
    });
    if (action === "new") {
      form.setFieldsValue({ type: setting?.routePlanType });
    }
    setState({
      ...state,
      action,
      routePlan: item,
      visibleModal: true,
    });
  };

  const handleOnFinish = async values => {
    if (state.routePlan) {
      if (state.action === "duplicate") {
        await handleDuplicateRoutePlan(values);
      } else {
        await handleUpdateRoutePlan(values);
      }
    } else {
      await handleCreateRoutePlan(values);
    }
    handleCancel();
  };

  const handleUpdateRoutePlan = async values => {
    await doUpdateRoutePlan({ id: state.routePlan._id, ...values });
  };

  const handleDuplicateRoutePlan = async values => {
    const { payload } = await doDuplicateRoutePlan({
      id: state.routePlan._id,
      ...values,
    });
    if (payload?._id) {
      history.push(`/route-plans/${payload._id}/orders`);
    }
  };

  const handleCreateRoutePlan = async values => {
    await doCreateRoutePlan(values);
  };

  const handleCancel = () => {
    setState({ ...state, visibleModal: false, action: null });
    form.resetFields();
  };

  const handleClickDelete = async item => {
    await doDeleteRoutePlan(item._id);
    await doGetAllRoutePlans({ currentPage, itemsPerPage, ...query });
  };

  const handleClickDuplicate = async item => {
    setState({
      ...state,
      action: "duplicate",
      routePlan: item,
      visibleModal: true,
    });
  };

  if (user.roles.includes("user")) {
    history.push("/orders");
    return <Spin style={{ paddingTop: 100, width: "100%" }} />;
  }

  return (
    <div className="Content RoutePlansPage">
      <RoutePlanCurrent />
      <div className="ContentHeader">
        <div className="HeaderTitle">{getTranslation(setting, "Route Plans", "Route Plans")}</div>
        <div className="HeaderToolBar">
          <div className="ToolBarButtons">
            {/* <Button className="FilterButton" type="primary">
              {getTranslation(setting, "Common", "Filter")}
              <ReactSVG className="icon" src="/icons/filter.svg" />
            </Button> */}
            {!companyDeactivated(setting?.status) && (
              <PrimaryButton
                label={getTranslation(setting, "Route Plans", "New Route Plan")}
                className="NewButton"
                onClick={() => handleClickAction("new", null)}
                icon={<ReactSVG className="icon" src="/icons/add.svg" />}
              />
            )}
          </div>
        </div>
      </div>
      <div className="ContentMain">
        <RoutePlansTable
          rowKey="_id"
          query={query}
          withAction={withAction}
          loading={isFetching}
          pagination={pagination}
          dataSource={routePlans}
          onChange2={handleTableChange}
          scrollHeight="calc(100vh - 285px)"
          actionWidth={150}
          setting={setting}
        />
      </div>
      <Modal open={state.visibleModal} onCancel={handleCancel} footer={[]} width={370}>
        <RoutePlanForm
          form={form}
          isFetching={isFetching}
          routePlan={state.routePlan}
          handleSubmit={handleOnFinish}
          handleCancel={handleCancel}
          isDuplicate={state.action === "duplicate"}
        />
      </Modal>
    </div>
  );
};

export default withPage(ListContainer(RoutePlansPage));
